/**
 * <s-reviews-carousel> — Horizontally scrollable review cards with prev/next and read-more.
 *
 * Commands (dispatched via commandfor/command on buttons pointing at this element's id):
 *   --prev   — Scroll back by one view.
 *   --next   — Scroll forward by one view.
 *   --toggle — Expand/collapse the review text in the source button's [data-review] card.
 *   --media-next — Show the next photo in the source button's card ([data-media] strip, [data-media-count] badge
 *                  with data-format "%current% / %total%" and data-total); wraps back to the first photo.
 *
 * Required child markup:
 *   [data-track]        — The native overflow-x scroll container (swipe works natively).
 *   [data-review]       — Each card; contains [data-review-text] and an optional [data-review-toggle].
 *   [data-review-text]  — Clamped text; the class in data-clamp-class is removed while expanded.
 *   [data-review-toggle] — The read-more button; hidden when the text already fits.
 *     data-label-more / data-label-less hold the translated labels.
 *   [data-prev] / [data-next] — Arrow buttons; disabled at the ends.
 */
class SReviewsCarousel extends HTMLElement {
  #ac;
  #track;
  #prev;
  #next;
  #resizeObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;
    this.#prev = this.querySelector('[data-prev]');
    this.#next = this.querySelector('[data-next]');
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.#scrollByView(-1);
      else if (event.command === '--next') this.#scrollByView(1);
      else if (event.command === '--toggle') this.#toggle(event.source);
      else if (event.command === '--media-next') this.#nextMedia(event.source);
    }, { signal });

    this.#track.addEventListener('scroll', () => this.#updateArrows(), { signal, passive: true });
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateToggles();
      this.#updateArrows();
    });
    this.#resizeObserver.observe(this.#track);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
  }

  #scrollByView(direction) {
    this.querySelector('s-auto-scroll')?.hold?.(4000);
    const card = this.#track.querySelector('[data-review]');
    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const step = card ? card.offsetWidth + gap : this.#track.clientWidth;
    const perView = Math.max(1, Math.floor((this.#track.clientWidth + gap) / step));
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({ left: direction * rtl * step * perView, behavior: smooth ? 'smooth' : 'auto' });
  }

  #updateArrows() {
    if (!this.#prev || !this.#next) return;
    const left = Math.abs(this.#track.scrollLeft);
    const max = this.#track.scrollWidth - this.#track.clientWidth - 1;
    this.#prev.disabled = left <= 0;
    this.#next.disabled = left >= max;
  }

  #updateToggles() {
    for (const card of this.#track.querySelectorAll('[data-review]')) {
      const text = card.querySelector('[data-review-text]');
      const toggle = card.querySelector('[data-review-toggle]');
      if (!text || !toggle) continue;
      if (toggle.getAttribute('aria-expanded') === 'true') continue;
      toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
    }
  }

  #nextMedia(button) {
    this.querySelector('s-auto-scroll')?.hold?.(4000);
    const card = button?.closest('[data-review]');
    const strip = card?.querySelector('[data-media]');
    if (!strip) return;
    const total = strip.children.length;
    if (total < 2) return;
    const width = strip.clientWidth;
    const current = Math.round(Math.abs(strip.scrollLeft) / width);
    const next = (current + 1) % total;
    const rtl = getComputedStyle(strip).direction === 'rtl' ? -1 : 1;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollTo({ left: rtl * next * width, behavior: smooth ? 'smooth' : 'auto' });
    const badge = card.querySelector('[data-media-count]');
    if (badge?.dataset.format) {
      badge.textContent = badge.dataset.format
        .replace('%current%', String(next + 1))
        .replace('%total%', String(total));
    }
  }

  #toggle(button) {
    const card = button?.closest('[data-review]');
    const text = card?.querySelector('[data-review-text]');
    if (!text) return;
    const clampClass = text.dataset.clampClass || 'line-clamp-6';
    const expanded = button.getAttribute('aria-expanded') === 'true';
    text.classList.toggle(clampClass, expanded);
    button.setAttribute('aria-expanded', String(!expanded));
    button.textContent = expanded ? button.dataset.labelMore : button.dataset.labelLess;
  }
}

if (!customElements.get('s-reviews-carousel')) {
  customElements.define('s-reviews-carousel', SReviewsCarousel);
}
